/**
 * Ticking Time Bomb - Limited-Time Free Patch Promotion
 *
 * Automatically adds a free patch to cart when a user adds any item.
 * Starts a countdown timer. When it expires, the patch is removed
 * and the user is permanently flagged so it never triggers again.
 */

// ═══════════════════════════════════════════════════════════════
// CONFIGURATION - Edit these values to control the system
// ═══════════════════════════════════════════════════════════════

const TTB_CONFIG = {
    // Master switch - set to false to disable everything
    enabled: true,

    // Sub-toggles UNDER `enabled`: independently gate the NEW-USER flow vs the
    // RETURNING-BUYER flow. Default true so an un-resaved theme behaves as today.
    // Panel-overridable via _flattenTtbApiConfig; the actual per-flow gate is in
    // activate() (where window.__userFlow is resolved). Add ONCE (literal = last-wins).
    enabledNewUser: true,
    enabledReturning: true,

    // Product handle of the patch to add to cart for NEW-USER flow (fallback / default for
    // any shopper whose country isn't in `patchHandlesByCountry` below, or
    // when `regionalEnabled` is false).
    patchHandle: 'red-scorpion-patch',

    // Automatic mode: derive `<country-slug>-flag-scorpion-patch` from the
    // shopper's country NAME (localStorage[country], e.g. "United States")
    // and use it IFF that product exists; otherwise fall back to the country
    // table, then patchHandle. Off by default. `{country}` is the slug token.
    automaticModeEnabled: false,
    automaticHandlePattern: '{country}-flag-scorpion-patch',

    // Product handle of the patch to add to cart for RETURNING-USER flow.
    // If empty or falsy, the new-user patchHandle is used (treats returning users
    // the same as new users). Set this to a distinct product handle to award
    // a separate patch to returning buyers.
    returningUserPatchHandle: 'reaper-scorpion-patch', // returning users get the reaper patch

    // Master switch for the country override. When false, every shopper gets
    // `patchHandle` regardless of the map. Default true to match the panel
    // config (the API value overrides this once it loads).
    regionalEnabled: true,

    // Country → patch-handle map. Keys match `localStorage[country]` (set by
    // recommendations.js from database-app-1). Lookup is case-insensitive
    // and trimmed. Multiple aliases for the same country need their own row.
    patchHandlesByCountry: {
        'United States': '4th-of-july-us-agilite-scorpion-patch',
        'Australia': 'australia-flag-scorpion-patch',
        'Canada': 'canada-flag-scorpion-patch'
    },

    // Country-specific patch handles for RETURNING-USER flow.
    // Keys match `localStorage[country]` (set by recommendations.js).
    // Lookup is case-insensitive and trimmed. If the map has no entry for the
    // current country, returningUserPatchHandle is used; if that's empty,
    // falls back to patchHandle.
    returningUserPatchHandlesByCountry: {
        // 'United States': 'returning-us-patch-handle'
    },

    countryOverride: {
        enabled: false,
        country: ''
    },

    // Panel-managed blacklist: product handles that must NEVER allow the TTB
    // free patch while present in the cart (checked cart-wide in activate()).
    excludedTriggerHandles: [],

    // Panel-managed blacklist (collection handles): if ANY cart item belongs to
    // one of these collections, the TTB free patch is suppressed. Lower-cased +
    // trimmed; membership lookup is cached + fail-open (see activate()).
    excludedTriggerCollections: [],

    // Panel-managed blacklist (product tags): if ANY cart item carries one of
    // these tags, the TTB free patch is suppressed. Lower-cased + trimmed; the
    // per-product tag lookup is cached + fail-open (see activate()).
    excludedTriggerTags: [],

    // Countdown duration in hours
    timerHours: 3,

    // localStorage keys
    storageKey: 'AGB_TTB_State',   // Active promotion state
    flagKey: 'AGB_TTB_Used',       // Permanent "already used" flag (new-user flow)
    returningUserFlagKey: 'AGB_TTB_Used_ReturningUser', // Permanent "already used" flag (returning-user flow)

    // Reset epoch (ms). When the panel bumps this, every shopper whose used-
    // flag was set BEFORE this time is treated as "not used yet" -> they see
    // the TTB again. The flag can't be deleted server-side; this invalidates
    // stale flags client-side. 0 = never reset.
    seenResetEpoch: 0,

    // UI toggles
    floatingBannerEnabled: true,    // Fixed banner at top of viewport
    lineItemTimerEnabled: true,     // Timer inside the cart line-item
    activationPopupEnabled: true,   // One-time popup when timer starts

    // Customizable display text ( {timer} is replaced with countdown )
    floatingBannerText: 'Free Patch Will Disappear in: {timer}',
    lineItemTimerText: 'Expires in {timer}',
    expiredText: 'Offer expired',
    activationPopupTitle: 'You Just Unlocked a FREE Patch',
    activationPopupMessage: 'Nice move - your patch is reserved right now. This is a one-time bonus and the countdown is live. When it hits zero, the patch is removed forever.',
    activationPopupHintText: 'Close cart and complete checkout now to lock it in before it disappears.',
    activationPopupButtonText: 'Secure My Patch Now',

    // Behavior
    removeOnExpiry: true,           // Auto-remove patch when timer hits 0

    // Debug mode - logs to console
    debug: false,

    // Preview mode - if set to a non-empty string, the system will ONLY
    // activate for users who have this key in localStorage.
    // Set in your browser console: localStorage.setItem('AGB_TTB_Preview', '1')
    // Leave empty '' to make it live for everyone.
    previewKey: '',
};

// ═══════════════════════════════════════════════════════════════
// END CONFIGURATION
// ═══════════════════════════════════════════════════════════════


// ─────────────────────────────────────────────────────────────────
// Runtime config: defaults above → localStorage cache → remote API.
// The remote-fetched config is admin-controlled in the control panel
// and overrides any value left at defaults. If the API is unreachable,
// we fall back to the cache; if both are unavailable, we use the
// hardcoded defaults above unchanged.
// ─────────────────────────────────────────────────────────────────

const TTB_SUPPORTED_SCHEMA_VERSIONS = [1];
// Live vs Preview storefront detection (mirrored from favorite-color-variant-picker.js).
// Each engine file is self-contained so it can load in any order. See the
// long comment in that file for what each branch does and why preview is the
// safe default.
function _agbDetectStorefrontEnv() {
    try {
        const urlForce = new URLSearchParams(location.search).get('storefront_env');
        if (urlForce === 'preview' || urlForce === 'live') return urlForce;
    } catch (_) {}
    try {
        const stored = localStorage.getItem('AGB_StorefrontEnv');
        if (stored === 'preview' || stored === 'live') return stored;
    } catch (_) {}
    const role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    if (role === 'main') return 'live';
    if (role === 'unpublished' || role === 'development' || role === 'demo') return 'preview';
    const host = (location && location.hostname) || '';
    if (host.endsWith('.shopifypreview.com')) return 'preview';
    if (host.endsWith('.myshopify.com')) return 'preview';
    if (host === '127.0.0.1' || host === 'localhost') return 'preview';
    try {
        if (new URLSearchParams(location.search).has('preview_theme_id')) return 'preview';
    } catch (_) {}
    return 'preview';
}

const TTB_ENV = _agbDetectStorefrontEnv();
const TTB_CACHE_KEY = `AGB_TTB_Config_${TTB_ENV}`;
const TTB_CACHE_ETAG = `AGB_TTB_ConfigEtag_${TTB_ENV}`;
const TTB_CACHE_FETCHED_AT = `AGB_TTB_ConfigFetchedAt_${TTB_ENV}`;

// ── Free-item blocklist (admin-managed deny list) ─────────────────────────
// The panel backend serves a PUBLIC (no-auth) list of entries shaped:
//   { product_handle, collection, tag, email, visitor_id, reason, created_at }
// with the invariant: at least one of {product_handle, collection, tag} AND at
// least one of {email, visitor_id}. An entry blocks the free patch when its
// "what" (handle / collection-membership / tag) matches the patch product AND
// its "who" (email / visitor_id) matches the shopper. We mirror the TTB config
// fetch pattern: ~5min localStorage cache, fail-open to an empty list on any
// error so the promo never silently breaks. The cache is read synchronously;
// the collection/tag membership lookups (when an entry carries them) are cached
// + fail-open; the network refresh is fire-and-forget.
const TTB_BLOCKLIST_CACHE_KEY = `AGB_TTB_Blocklist_${TTB_ENV}`;
const TTB_BLOCKLIST_FETCHED_AT = `AGB_TTB_BlocklistFetchedAt_${TTB_ENV}`;
const TTB_BLOCKLIST_TTL_MS = 5 * 60 * 1000;

/** Flatten the nested API shape (ui.X, text.X, behavior.X) into the
 *  flat shape TTB_CONFIG actually uses at runtime. */
function _flattenTtbApiConfig(api) {
    if (!api || typeof api !== 'object') return null;
    const flat = {};
    if (typeof api.enabled === 'boolean') flat.enabled = api.enabled;
    if (typeof api.enabledNewUser === 'boolean') flat.enabledNewUser = api.enabledNewUser;
    if (typeof api.enabledReturning === 'boolean') flat.enabledReturning = api.enabledReturning;
    if (typeof api.patchHandle === 'string' && api.patchHandle.trim()) flat.patchHandle = api.patchHandle.trim();
    if (typeof api.returningUserPatchHandle === 'string') flat.returningUserPatchHandle = api.returningUserPatchHandle.trim();
    if (typeof api.automaticModeEnabled === 'boolean') flat.automaticModeEnabled = api.automaticModeEnabled;
    if (typeof api.automaticHandlePattern === 'string' && api.automaticHandlePattern.trim()) flat.automaticHandlePattern = api.automaticHandlePattern.trim();
    if (typeof api.seenResetEpoch === 'number' && api.seenResetEpoch >= 0) flat.seenResetEpoch = api.seenResetEpoch;
    if (typeof api.regionalEnabled === 'boolean') flat.regionalEnabled = api.regionalEnabled;
    if (api.patchHandlesByCountry && typeof api.patchHandlesByCountry === 'object' && !Array.isArray(api.patchHandlesByCountry)) {
        const map = {};
        for (const [country, handle] of Object.entries(api.patchHandlesByCountry)) {
            if (typeof country === 'string' && typeof handle === 'string') {
                const c = country.trim();
                const h = handle.trim();
                if (c && h) map[c] = h;
            }
        }
        flat.patchHandlesByCountry = map;
    }
    if (api.returningUserPatchHandlesByCountry && typeof api.returningUserPatchHandlesByCountry === 'object' && !Array.isArray(api.returningUserPatchHandlesByCountry)) {
        const map = {};
        for (const [country, handle] of Object.entries(api.returningUserPatchHandlesByCountry)) {
            if (typeof country === 'string' && typeof handle === 'string') {
                const c = country.trim();
                const h = handle.trim();
                if (c && h) map[c] = h;
            }
        }
        flat.returningUserPatchHandlesByCountry = map;
    }
    if (typeof api.timerHours === 'number' && api.timerHours > 0) flat.timerHours = api.timerHours;
    if (api.ui && typeof api.ui === 'object') {
        for (const k of ['floatingBannerEnabled', 'lineItemTimerEnabled', 'activationPopupEnabled']) {
            if (typeof api.ui[k] === 'boolean') flat[k] = api.ui[k];
        }
    }
    if (api.text && typeof api.text === 'object') {
        for (const k of [
            'floatingBannerText', 'lineItemTimerText', 'expiredText',
            'activationPopupTitle', 'activationPopupMessage',
            'activationPopupHintText', 'activationPopupButtonText'
        ]) {
            if (typeof api.text[k] === 'string' && api.text[k].length > 0) flat[k] = api.text[k];
        }
    }
    if (api.behavior && typeof api.behavior === 'object') {
        if (typeof api.behavior.removeOnExpiry === 'boolean') flat.removeOnExpiry = api.behavior.removeOnExpiry;
        if (typeof api.behavior.debug === 'boolean') flat.debug = api.behavior.debug;
        if (typeof api.behavior.previewKey === 'string') flat.previewKey = api.behavior.previewKey;
    }
    if (api.countryOverride && typeof api.countryOverride === 'object') {
        flat.countryOverride = {
            enabled: !!api.countryOverride.enabled,
            country: typeof api.countryOverride.country === 'string' ? api.countryOverride.country.trim() : ''
        };
    }
    if (Array.isArray(api.excludedTriggerHandles)) {
        flat.excludedTriggerHandles = api.excludedTriggerHandles
            .map(function (h) { return String(h == null ? '' : h).trim().toLowerCase(); })
            .filter(Boolean);
    }
    if (Array.isArray(api.excludedTriggerCollections)) {
        flat.excludedTriggerCollections = api.excludedTriggerCollections
            .map(function (h) { return String(h == null ? '' : h).trim().toLowerCase(); })
            .filter(Boolean);
    }
    if (Array.isArray(api.excludedTriggerTags)) {
        flat.excludedTriggerTags = api.excludedTriggerTags
            .map(function (t) { return String(t == null ? '' : t).trim().toLowerCase(); })
            .filter(Boolean);
    }
    return flat;
}

// Preview-only visual confirmation that the Country Simulator is active, so a
// QA tester can SEE the override is in effect (it otherwise only changes the
// TTB patch resolution silently). Gated on TTB_ENV === 'preview' (the override
// is preview-scoped + live-scrubbed), shows once per page, click/auto-dismiss.
var _ctryOverrideToastShown = false;
function _maybeShowCountryOverrideToast() {
    try {
        if (_ctryOverrideToastShown) return;
        var ov = TTB_CONFIG.countryOverride;
        if (!ov || !ov.enabled || !ov.country) return;
        if (TTB_ENV !== 'preview') return;
        if (typeof document === 'undefined') return;
        if (!document.body) {
            document.addEventListener('DOMContentLoaded', _maybeShowCountryOverrideToast, { once: true });
            return;
        }
        _ctryOverrideToastShown = true;
        var el = document.createElement('div');
        el.setAttribute('data-ttb-country-toast', '1');
        el.textContent = '🌍 Country overridden → ' + ov.country + '  ·  TTB preview';
        el.style.cssText = [
            'position:fixed', 'z-index:2147483647', 'left:14px', 'bottom:14px',
            'background:#111827', 'color:#fbbf24',
            'font:600 12px/1.4 system-ui,-apple-system,sans-serif',
            'padding:9px 13px', 'border-radius:9px', 'border:1px solid #fbbf24',
            'box-shadow:0 8px 24px rgba(0,0,0,.4)', 'max-width:340px', 'cursor:pointer'
        ].join(';');
        el.title = 'Country Simulator is ON (preview): the Ticking Time Bomb resolves the patch as if every shopper is in ' + ov.country + '. Click to dismiss.';
        el.addEventListener('click', function () { try { el.remove(); } catch (e) {} });
        document.body.appendChild(el);
        setTimeout(function () { try { el.remove(); } catch (e) {} }, 12000);
    } catch (e) {}
}

function _applyTtbOverrides(flat) {
    if (!flat) return;
    for (const k of Object.keys(flat)) {
        TTB_CONFIG[k] = flat[k];
    }
    _maybeShowCountryOverrideToast();
}

function _loadCachedTtbConfig() {
    try {
        if (typeof localStorage === 'undefined') return;
        const raw = localStorage.getItem(TTB_CACHE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || !TTB_SUPPORTED_SCHEMA_VERSIONS.includes(parsed.schemaVersion)) {
            // Schema mismatch — wipe so the next fetch can repopulate cleanly.
            localStorage.removeItem(TTB_CACHE_KEY);
            localStorage.removeItem(TTB_CACHE_ETAG);
            localStorage.removeItem(TTB_CACHE_FETCHED_AT);
            return;
        }
        _applyTtbOverrides(_flattenTtbApiConfig(parsed));
    } catch (e) { /* corrupt cache — keep defaults */ }
}

async function _fetchTtbConfigFromAPI() {
    const TTL_MS = 5 * 60 * 1000;
    const BASE = (typeof window !== 'undefined' && window.AGB_TTB_CONFIG_URL)
        || 'http://localhost:8000/api/ttb-config';
    const ENDPOINT = TTB_ENV === 'preview'
        ? `${BASE}${BASE.indexOf('?') === -1 ? '?' : '&'}env=preview`
        : BASE;
    try {
        if (typeof localStorage === 'undefined') return;
        const lastFetched = parseInt(localStorage.getItem(TTB_CACHE_FETCHED_AT) || '0', 10);
        if (Date.now() - lastFetched < TTL_MS) return;

        const etag = localStorage.getItem(TTB_CACHE_ETAG);
        const headers = { 'Accept': 'application/json' };
        if (etag) headers['If-None-Match'] = etag;

        const res = await fetch(ENDPOINT, { headers });
        localStorage.setItem(TTB_CACHE_FETCHED_AT, String(Date.now()));

        if (res.status === 304) return;
        if (!res.ok) return;

        const body = await res.json();
        if (!body || !body.config) return;
        if (!TTB_SUPPORTED_SCHEMA_VERSIONS.includes(body.schemaVersion)) return;

        // Persist the canonical (nested) form to cache; flatten only on read.
        localStorage.setItem(TTB_CACHE_KEY, JSON.stringify({ schemaVersion: body.schemaVersion, ...body.config }));
        const newEtag = res.headers.get('ETag');
        if (newEtag) localStorage.setItem(TTB_CACHE_ETAG, newEtag);

        // Don't hot-swap mid-session — TTB DOM is stateful (popup shown once,
        // running countdown). New values apply on next page load.
    } catch (e) { /* network/JSON error — keep cache */ }
}

// Read the cached blocklist (sync). Returns an array of entries, [] on miss
// or any error. Fail-open is intentional: a corrupt/absent cache must never
// block the promo.
function _readCachedBlocklist() {
    try {
        if (typeof localStorage === 'undefined') return [];
        const raw = localStorage.getItem(TTB_BLOCKLIST_CACHE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
}

// Fire-and-forget refresh of the free-item blocklist. Mirrors
// _fetchTtbConfigFromAPI: same panel-backend base URL, ~5min localStorage
// cache, fail-open (leaves the existing cache untouched on any error).
async function _fetchBlocklistFromAPI() {
    const BASE = (typeof window !== 'undefined' && window.AGB_FREE_ITEM_BLOCKLIST_URL)
        || 'http://localhost:8000/api/free-item-blocklist';
    try {
        if (typeof localStorage === 'undefined') return;
        const lastFetched = parseInt(localStorage.getItem(TTB_BLOCKLIST_FETCHED_AT) || '0', 10);
        if (Date.now() - lastFetched < TTB_BLOCKLIST_TTL_MS) return;

        const res = await fetch(BASE, { headers: { 'Accept': 'application/json' } });
        // Stamp the fetch time regardless so a flaky endpoint isn't hammered.
        localStorage.setItem(TTB_BLOCKLIST_FETCHED_AT, String(Date.now()));
        if (!res.ok) return;

        const body = await res.json();
        const entries = (body && Array.isArray(body.entries)) ? body.entries : [];
        localStorage.setItem(TTB_BLOCKLIST_CACHE_KEY, JSON.stringify(entries));
    } catch (e) { /* network/JSON error — keep cache (fail-open) */ }
}

// Returns true if the free patch for product `P` is blocked for the current
// shopper. An entry blocks iff its "what" matches the product AND its "who"
// matches the shopper:
//   whatMatches = (product_handle && P.handle===product_handle)
//              OR (collection && P is in that collection)
//              OR (tag && P.tags includes that tag)
//   userMatches = (email && email.lower===userEmail.lower)
//              OR (visitor_id && visitor_id===userVisitorId)
// Handles/collections/tags are compared lower-cased + trimmed. `helpers`
// supplies the cached collection-membership / product-tags lookups (closure-
// scoped). ASYNC + FAIL-OPEN: any error -> false (not blocked, allow the patch).
async function _isFreeItemBlocked(handle, email, visitorId, helpers) {
    try {
        const productHandle = String(handle == null ? '' : handle).trim().toLowerCase();
        if (!productHandle) return false;

        // SECURITY: we no longer download the blocklist (it holds admin PII —
        // emails + visitor_ids). Instead we ask the backend PER (shopper,
        // product): send the shopper identity + this product's OWN collections
        // (server-emitted catalog) + tags, and it replies {blocked}. The roster
        // never reaches the browser. Both lookups are defensive — a gap just
        // means a collection/tag RULE can't match (fail-open); handle + identity
        // rules still work.
        let collections = [];
        try {
            if (typeof window !== 'undefined' && window.Catalog && typeof window.Catalog.collectionsOf === 'function') {
                collections = window.Catalog.collectionsOf(productHandle) || [];
            }
        } catch (e) { /* ignore */ }
        let tags = [];
        try {
            const productTags = (helpers && typeof helpers.productTags === 'function') ? helpers.productTags : null;
            if (productTags) tags = (await productTags(productHandle)) || [];
        } catch (e) { /* ignore */ }

        const BASE = (typeof window !== 'undefined' && window.AGB_FREE_ITEM_BLOCKLIST_URL)
            || 'http://localhost:8000/api/free-item-blocklist';
        const res = await fetch(BASE + '/check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                handle: productHandle,
                email: (typeof email === 'string' && email) ? email : null,
                visitor_id: visitorId || null,
                collections: collections,
                tags: tags,
            }),
        });
        if (!res.ok) return false;            // fail-open (never block a legit shopper)
        const body = await res.json();
        return !!(body && body.blocked);
    } catch (e) { return false; }             // fail-open
}

// Apply the cached config (sync) BEFORE any of the early-return checks below
// run — so a fresh page-load reflects the admin's latest saved values.
_loadCachedTtbConfig();

// Module-level remote refresh — MUST run even when the baked default is
// enabled:false, else a fresh LIVE browser bails at the IIFE's
// `if (!TTB_CONFIG.enabled) return;` BEFORE ever seeding AGB_TTB_Config_<env>,
// so the panel's enabled:true could never bootstrap (the live-vs-preview
// "panel says on, live stays off" deadlock). No hot-swap: this only writes the
// cache; _loadCachedTtbConfig applies it on the NEXT page load.
_fetchTtbConfigFromAPI();

/**
 * Resolve the patch handle to use for this shopper, consulting (in order):
 *   1. `regionalEnabled` — if false, always returns `TTB_CONFIG.patchHandle`
 *   2. `localStorage[country]` (set by recommendations.js) → case-insensitive
 *      lookup in `patchHandlesByCountry`
 *   3. fallback to `TTB_CONFIG.patchHandle`
 *
 * Re-evaluated at every call site that needs a handle, so config refreshes
 * (and a late-arriving country from recommendations.js) take effect on the
 * next cart-add / cart-check without needing a page reload.
 */
function _resolveTtbCountry() {
    // Dev-only override takes precedence over the real localStorage value.
    // The admin panel locks editing of this block unless connected to a
    // localhost backend — see CountrySimulatorSection in the panel UI.
    var override = TTB_CONFIG.countryOverride;
    // Defense in depth: the Country Simulator is a DEV/QA-only tool and must
    // NEVER affect real shoppers, even if a bad countryOverride.enabled:true
    // leaks into the LIVE config envelope (the backend live-scrub is the first
    // line; this storefront env-gate is the second). On the live storefront the
    // override is ignored entirely, so resolution falls through to the real
    // localStorage['country'] set by recommendations.js.
    if (TTB_ENV !== 'live' && override && override.enabled && typeof override.country === 'string') {
        var forced = override.country.trim();
        if (forced) return forced;
    }
    try { return (localStorage.getItem('country') || '').trim(); } catch (e) { return ''; }
}

// ── Automatic country-flag patch resolution ──────────────────────────────
//
// When `automaticModeEnabled`, derive a patch handle from the shopper's
// country NAME via `automaticHandlePattern` ('{country}' -> slug) and use it
// IFF that product actually exists on the storefront. The existence check is
// async; results are cached (memory + sessionStorage) and probed on demand.
// Until a probe resolves, resolution falls through to the country table /
// default so a non-existent patch is never auto-added.

var _ttbHandleExists = Object.create(null);   // handle -> true | false | 'pending'
var TTB_EXISTS_PREFIX = 'AGB_TTB_AutoExists:';

function _ttbSlugifyCountry(name) {
    var s = String(name == null ? '' : name).toLowerCase();
    try { s = s.normalize('NFKD').replace(/[̀-ͯ]/g, ''); } catch (e) {}
    return s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function _ttbAutoHandleFor(country) {
    var slug = _ttbSlugifyCountry(country);
    if (!slug) return '';
    var pattern = (typeof TTB_CONFIG.automaticHandlePattern === 'string' && TTB_CONFIG.automaticHandlePattern)
        ? TTB_CONFIG.automaticHandlePattern
        : '{country}-flag-scorpion-patch';
    return pattern.indexOf('{country}') !== -1 ? pattern.replace('{country}', slug) : pattern.trim();
}

function _ttbProbeHandleExists(handle) {
    if (!handle) return;
    var cur = _ttbHandleExists[handle];
    if (cur === true || cur === false || cur === 'pending') return;
    // sessionStorage cache (per tab) so each handle is probed at most once.
    try {
        var ss = sessionStorage.getItem(TTB_EXISTS_PREFIX + handle);
        if (ss === '1') { _ttbHandleExists[handle] = true; return; }
        if (ss === '0') { _ttbHandleExists[handle] = false; return; }
    } catch (e) {}
    // Prefer the server-emitted catalog (sync, no network, sees hidden products).
    try {
        if (window.Catalog && typeof window.Catalog.get === 'function' && window.Catalog.get(handle)) {
            _ttbHandleExists[handle] = true;
            try { sessionStorage.setItem(TTB_EXISTS_PREFIX + handle, '1'); } catch (e) {}
            return;
        }
    } catch (e) {}
    // Skip a handle already known-dead (cart-quantity-cap tracks /products/<h>.js 404s).
    try {
        if (typeof window.AGB_CartCap_isDeadHandle === 'function' && window.AGB_CartCap_isDeadHandle(handle)) {
            _ttbHandleExists[handle] = false;
            try { sessionStorage.setItem(TTB_EXISTS_PREFIX + handle, '0'); } catch (e) {}
            return;
        }
    } catch (e) {}
    _ttbHandleExists[handle] = 'pending';
    fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) {
            var exists = !!(r && r.ok);
            _ttbHandleExists[handle] = exists;
            try { sessionStorage.setItem(TTB_EXISTS_PREFIX + handle, exists ? '1' : '0'); } catch (e) {}
        })
        .catch(function () { _ttbHandleExists[handle] = false; });
}

// Warm the automatic-mode existence probe (init + post-config) so the derived
// country patch is usually resolved before the shopper's first cart-add.
function _ttbWarmAutoHandle() {
    if (!TTB_CONFIG.automaticModeEnabled) return;
    var country = _resolveTtbCountry();
    if (!country) return;
    var auto = _ttbAutoHandleFor(country);
    if (auto) _ttbProbeHandleExists(auto);
}

function _resolveTtbPatchHandle() {
    // RETURNING-USER FLOW GATE: returning buyers (window.__userFlow ===
    // 'returning-user') resolve a distinct base handle + table; new users use
    // the default pair. Automatic mode (below) applies to whichever is active.
    var isReturningUser = (typeof window !== 'undefined' && window.__userFlow === 'returning-user');
    var baseHandle = isReturningUser ? TTB_CONFIG.returningUserPatchHandle : TTB_CONFIG.patchHandle;
    var baseMap = isReturningUser ? TTB_CONFIG.returningUserPatchHandlesByCountry : TTB_CONFIG.patchHandlesByCountry;
    var fallback = baseHandle || TTB_CONFIG.patchHandle; // empty returning handle -> new-user handle
    var country = _resolveTtbCountry();

    // 1) AUTOMATIC — `<country-slug>-flag-scorpion-patch` if that product
    //    exists. Async existence probe; until it confirms we fall through so a
    //    missing patch is never added. Independent of regionalEnabled.
    if (TTB_CONFIG.automaticModeEnabled && country) {
        var auto = _ttbAutoHandleFor(country);
        if (auto) {
            var st = _ttbHandleExists[auto];
            if (st === true) return auto;
            if (st === undefined) _ttbProbeHandleExists(auto); // kick off; fall through this call
            // st === false / 'pending' -> fall through to the table / default
        }
    }

    // 2) TABLE — country -> patch map (existing behaviour, gated by regionalEnabled).
    if (TTB_CONFIG.regionalEnabled && country && baseMap && typeof baseMap === 'object') {
        var needle = country.toLowerCase();
        for (var key in baseMap) {
            if (Object.prototype.hasOwnProperty.call(baseMap, key) && typeof key === 'string') {
                if (key.trim().toLowerCase() === needle) {
                    var v = (baseMap[key] || '').trim();
                    if (v) return v;
                }
            }
        }
    }

    // 3) DEFAULT
    return fallback;
}

(function () {
    'use strict';

    if (!TTB_CONFIG.enabled) return;

    // Preview gate — if previewKey is set, only run for users who have it in localStorage
    if (TTB_CONFIG.previewKey) {
        try {
            if (localStorage.getItem(TTB_CONFIG.previewKey) !== '1') return;
        } catch (e) {
            return;
        }
    }

    // (Config refresh is hoisted to module top level — see _fetchTtbConfigFromAPI
    // call right after _loadCachedTtbConfig — so it runs even when the baked
    // default is enabled:false. Don't re-add it here.)

    // (Removed: the blocklist is no longer downloaded into the browser — it held
    // admin PII. _isFreeItemBlocked() now asks the backend per (shopper, product)
    // via /api/free-item-blocklist/check. Clear any stale cached copy from when
    // the full list was downloaded.)
    try { localStorage.removeItem(TTB_BLOCKLIST_CACHE_KEY); localStorage.removeItem(TTB_BLOCKLIST_FETCHED_AT); } catch (e) {}

    // Warm the automatic-mode existence probe so the derived country patch is
    // usually resolved before the shopper's first cart-add (no-op unless
    // automaticModeEnabled + a known country). Late-arriving country is still
    // covered by the lazy probe inside _resolveTtbPatchHandle.
    try { _ttbWarmAutoHandle(); } catch (e) {}

    // ── State helpers ──────────────────────────────────────────

    function log(...args) {
        // Print when TTB's own debug flag is on OR the panel's preview-only
        // `scarcity_ttb` console category is enabled — same self-gated pattern
        // cae-client uses for `cae_event` (window.AGB_consoleCat). This makes
        // the TTB analytics event (and the rest of the [TTB] trail) show in F12
        // alongside the other events when that category is toggled on. NEVER
        // prints on live: AGB_consoleCat is false off-preview.
        var on = TTB_CONFIG.debug;
        if (!on) {
            try { on = !!(window.AGB_consoleCat && window.AGB_consoleCat('scarcity_ttb')); } catch (e) {}
        }
        if (on) console.log('[TTB]', ...args);
    }

    function loadState() {
        try {
            const raw = localStorage.getItem(TTB_CONFIG.storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    function saveState(state) {
        try {
            localStorage.setItem(TTB_CONFIG.storageKey, JSON.stringify(state));
        } catch (e) {
            // best effort
        }
    }

    function clearState() {
        try {
            localStorage.removeItem(TTB_CONFIG.storageKey);
            localStorage.removeItem(TTB_CONFIG.storageKey + '_BDismissed');
        } catch (e) {
            // best effort
        }
    }

    function isUsed() {
        try {
            // RETURNING-USER FLOW GATE: check which flag applies based on window.__userFlow.
            var isReturningUser = (typeof window !== 'undefined' && window.__userFlow === 'returning-user');
            var flagKey = isReturningUser ? TTB_CONFIG.returningUserFlagKey : TTB_CONFIG.flagKey;
            var raw = localStorage.getItem(flagKey);
            if (!raw) return false;
            // Legacy '1' flags carry no timestamp -> treat as set at time 0, so a
            // reset (seenResetEpoch > 0) invalidates them. New flags store the
            // ms timestamp they were set at.
            var flagAt = (raw === '1') ? 0 : (parseInt(raw, 10) || 0);
            var resetAt = (typeof TTB_CONFIG.seenResetEpoch === 'number') ? TTB_CONFIG.seenResetEpoch : 0;
            // "Used" only if the flag was set AT/AFTER the last reset; a flag
            // predating the reset is stale -> shopper sees the TTB again.
            return flagAt >= resetAt;
        } catch (e) {
            return false;
        }
    }

    function markUsed() {
        try {
            // RETURNING-USER FLOW GATE: set the flag that applies based on window.__userFlow.
            var isReturningUser = (typeof window !== 'undefined' && window.__userFlow === 'returning-user');
            var flagKey = isReturningUser ? TTB_CONFIG.returningUserFlagKey : TTB_CONFIG.flagKey;
            // Store the time the flag was set (ms) so a later seenResetEpoch can
            // invalidate it. isUsed() still understands the legacy '1' value.
            localStorage.setItem(flagKey, String(Date.now()));
            // Cross-device bridge: nudge the scarcity engine to persist NOW so
            // this flag rides its group-scoped backend save/restore (the engine
            // reads these localStorage keys at save time and bridges them back
            // on restore). Fail-open: a missing/older engine must never block
            // the promo's own used-flag write above.
            try {
                if (window.SE_Engine && typeof window.SE_Engine.saveState === 'function') {
                    window.SE_Engine.saveState('ttb_used');
                }
            } catch (e) {}
        } catch (e) {
            // best effort
        }
    }

    // ── Cart API helpers ───────────────────────────────────────

    async function getCart() {
        try {
            const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) {
            return null;
        }
    }

    async function addPatchToCart(triggeringItem, timerStartedAt) {
        const handle = _resolveTtbPatchHandle();

        // ── Admin free-item blocklist gate ───────────────────────────────
        // Silently skip awarding the free patch if this shopper (matched by
        // logged-in email or visitorID) is on the panel-managed blocklist for
        // THIS patch handle. FAIL-OPEN: any error here must not block the
        // promo, so the whole check is wrapped and resolves to "not blocked".
        try {
            const visitorId = localStorage.getItem('visitorID');
            const email = (window.AGB_Customer && window.AGB_Customer.email) || null;
            const blocked = await _isFreeItemBlocked(handle, email, visitorId, {
                collectionHandleSet: _ttbCollectionHandleSet,
                productTags: _ttbProductTags,
            });
            if (blocked) {
                log('Free patch blocked for this shopper (blocklist):', handle, { email: email, visitorId: visitorId });
                return null;
            }
        } catch (e) { /* fail-open — never block the promo on a check error */ }

        try {
            // 1. Fetch product to get variant ID
            log('Fetching product data for:', handle);
            const prodRes = await fetch(`/products/${handle}.js`);
            if (!prodRes.ok) {
                log('Product fetch failed:', prodRes.status, prodRes.statusText);
                return null;
            }
            const product = await prodRes.json();

            if (!product || !product.variants || product.variants.length === 0) {
                log('Product has no variants:', product?.title);
                return null;
            }

            const variant = product.variants.find(v => v.available) || product.variants[0];
            if (!variant || !variant.id) {
                log('No valid variant found. Available variants:', product.variants.length);
                return null;
            }

            log('Adding variant to cart:', variant.id, variant.title);

            // 2. Add to cart
            const addRes = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items: [{
                    id: variant.id,
                    quantity: 1,
                    properties: {
                        '_ttb_promo': 'true',
                        '_ttb_added_at': new Date().toISOString(),
                        '_ttb_timer_started_at': new Date(timerStartedAt).toISOString(),
                        '_ttb_timer_expires_at': new Date(timerStartedAt + getTimerMs()).toISOString(),
                        '_ttb_triggered_by_handle': triggeringItem ? (triggeringItem.product_handle || triggeringItem.handle || '') : '',
                        '_ttb_triggered_by_title': triggeringItem ? (triggeringItem.product_title || triggeringItem.title || '') : '',
                    },
                }] }),
            });

            if (!addRes.ok) {
                const errorText = await addRes.text().catch(() => '');
                log('Cart add failed:', addRes.status, addRes.statusText, errorText);
                return null;
            }
            await addRes.json();

            // 3. Small delay then get cart to find the line key
            await new Promise(r => setTimeout(r, 300));
            const cart = await getCart();
            if (!cart || !cart.items) {
                log('Could not fetch cart after adding patch');
                return null;
            }

            const patchItem = cart.items.find(i => (i.handle === handle || i.product_handle === handle));
            if (!patchItem) {
                log('Patch not found in cart after adding. Cart items:', cart.items.map(i => i.product_handle));
                return null;
            }

            // Get image URL from product data or cart item
            const imageUrl = product.featured_image || patchItem.featured_image?.url || patchItem.image || '';

            log('Patch added successfully, line key:', patchItem.key);
            return { lineKey: patchItem.key, cart, imageUrl };
        } catch (e) {
            log('Error in addPatchToCart:', e);
            return null;
        }
    }

    async function removePatchFromCart(lineKey) {
        try {
            // A STORED line key goes stale after any cart mutation, and is simply
            // gone if the shopper already removed the patch — POSTing a stale or
            // missing id to /cart/change.js returns 400 (the recurring bad request).
            // So resolve the patch's CURRENT line from the live cart by its
            // _ttb_promo property; if the patch isn't in the cart there's nothing to
            // remove, so skip the request entirely (no 400).
            const cart = await getCart();
            if (!cart || !Array.isArray(cart.items)) return;
            let line = null;
            for (let i = 0; i < cart.items.length; i++) {
                const it = cart.items[i];
                if (it && it.properties && it.properties._ttb_promo === 'true') { line = it; break; }
            }
            if (!line && lineKey) {
                for (let i = 0; i < cart.items.length; i++) {
                    if (cart.items[i] && cart.items[i].key === lineKey) { line = cart.items[i]; break; }
                }
            }
            if (!line) return; // patch not in cart → nothing to remove, no request
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({ id: line.key, quantity: 0 }),
            });
        } catch (e) {
            // best effort
        }
    }

    // ── Cached collection / tag lookups (fail-open) ──────────────────────────
    // Shared by the cart-wide TTB exclusion (excludedTriggerCollections /
    // excludedTriggerTags) and the per-collection free-patches-gifts check. Both
    // cache per-collection / per-handle in sessionStorage so repeated cart-adds
    // in a tab don't re-hit the network. ANY error resolves to "empty" so a
    // fetch failure never falsely suppresses a legit free patch.
    const _ttbCollHandleSetMem = Object.create(null);   // collHandle -> Set<productHandle>
    const _ttbProductTagsMem = Object.create(null);     // productHandle -> string[]
    const TTB_COLL_CACHE_PREFIX = 'AGB_TTB_CollHandles:';
    const TTB_TAGS_CACHE_PREFIX = 'AGB_TTB_ProductTags:';

    // Returns a Set of product handles in the given collection. Cached per
    // collection (memory + sessionStorage). FAIL-OPEN: any error -> empty Set.
    async function _ttbCollectionHandleSet(collectionHandle) {
        const h = String(collectionHandle == null ? '' : collectionHandle).trim().toLowerCase();
        if (!h) return new Set();
        if (_ttbCollHandleSetMem[h]) return _ttbCollHandleSetMem[h];
        try {
            const ss = sessionStorage.getItem(TTB_COLL_CACHE_PREFIX + h);
            if (ss) {
                const arr = JSON.parse(ss);
                if (Array.isArray(arr)) {
                    const set = new Set(arr);
                    _ttbCollHandleSetMem[h] = set;
                    return set;
                }
            }
        } catch (e) { /* ignore cache read errors, fall through to fetch */ }
        try {
            const res = await fetch(
                `/collections/${encodeURIComponent(h)}/products.json?limit=250`,
                { headers: { Accept: 'application/json' } }
            );
            if (!res.ok) return new Set();
            const data = await res.json();
            const handles = (data?.products || [])
                .map(p => String(p && p.handle ? p.handle : '').trim().toLowerCase())
                .filter(Boolean);
            const set = new Set(handles);
            _ttbCollHandleSetMem[h] = set;
            try { sessionStorage.setItem(TTB_COLL_CACHE_PREFIX + h, JSON.stringify(handles)); } catch (e) {}
            return set;
        } catch (e) {
            log('Collection handle-set fetch failed (fail-open):', e.message || e);
            return new Set();
        }
    }

    // Returns the product's tags (lower-cased) for the given handle. Cached per
    // handle (memory + sessionStorage). FAIL-OPEN: any error -> empty array.
    async function _ttbProductTags(handle) {
        const h = String(handle == null ? '' : handle).trim().toLowerCase();
        if (!h) return [];
        if (_ttbProductTagsMem[h]) return _ttbProductTagsMem[h];
        try {
            const ss = sessionStorage.getItem(TTB_TAGS_CACHE_PREFIX + h);
            if (ss) {
                const arr = JSON.parse(ss);
                if (Array.isArray(arr)) {
                    _ttbProductTagsMem[h] = arr;
                    return arr;
                }
            }
        } catch (e) { /* ignore cache read errors, fall through to fetch */ }
        try {
            const res = await fetch(`/products/${encodeURIComponent(h)}.js`, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' }
            });
            if (!res.ok) return [];
            const product = await res.json();
            const tags = (Array.isArray(product?.tags) ? product.tags : [])
                .map(t => String(t == null ? '' : t).trim().toLowerCase())
                .filter(Boolean);
            _ttbProductTagsMem[h] = tags;
            try { sessionStorage.setItem(TTB_TAGS_CACHE_PREFIX + h, JSON.stringify(tags)); } catch (e) {}
            return tags;
        } catch (e) {
            log('Product tags fetch failed (fail-open):', e.message || e);
            return [];
        }
    }

    // Returns true if ANY item in the provided array belongs to the 'free-patches-gifts' collection.
    // Does a single collection fetch regardless of how many items are checked.
    // Fails open (returns false) so a network error never silently blocks the promotion.
    async function isAnyItemInExcludedCollection(items) {
        if (!items || items.length === 0) return false;
        try {
            const excludedHandles = await _ttbCollectionHandleSet('free-patches-gifts');
            if (!excludedHandles.size) return false;
            return items.some(i => excludedHandles.has(
                String(i.product_handle || i.handle || '').trim().toLowerCase()
            ));
        } catch (e) {
            log('Collection exclusion check failed (fail-open):', e.message || e);
            return false;
        }
    }


    async function sendActivationTrackingEvent(state, triggeringItem, cart, lineKey, imageUrl) {
        try {
            // Fake Customer Engine: a synthetic visitor still GETS the free patch
            // (the workflow), but its activation is NOT recorded to ttb_events /
            // BigQuery — drop the analytics event entirely.
            try { if (window.SE_isFakeVisitor && window.SE_isFakeVisitor()) { log('fake visitor — TTB event suppressed'); return; } } catch (_) {}
            let visitorId = null;
            try { visitorId = localStorage.getItem('visitorID') || null; } catch (_) {}

            const payload = {
                event: 'ttb_activated',
                timestamp: new Date(state.addedAt).toISOString(),
                visitor_id: visitorId,
                url: window.location.href,
                referrer: document.referrer || '',
                cart_token: cart?.token || '',
                timer_hours: TTB_CONFIG.timerHours,
                timer_expires_at: new Date(state.addedAt + getTimerMs()).toISOString(),
                triggering_product: triggeringItem ? {
                    handle: triggeringItem.product_handle || triggeringItem.handle || '',
                    title: triggeringItem.product_title || triggeringItem.title || '',
                    variant_id: triggeringItem.variant_id || null,
                    variant_title: triggeringItem.variant_title || '',
                    quantity: triggeringItem.quantity || 1,
                    price_cents: triggeringItem.price || 0,
                } : null,
                free_patch: {
                    handle: _resolveTtbPatchHandle(),
                    line_key: lineKey,
                    image_url: imageUrl,
                },
            };
            log('Sending event:', payload);
            await fetch('https://analytics-ingest-432657747828.europe-west1.run.app/ttb/event', {  // GCP-native ingest: Cloud Run -> Pub/Sub -> BigQuery events. (Was database-app-1 on Render.)
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                // keepalive: TTB activation happens right before the shopper
                // navigates to checkout; without this the in-flight POST is
                // CANCELLED on page unload and the event is lost (the #1 reason
                // TTB events "don't trigger well"). keepalive lets the browser
                // finish the send after navigation (64KB cap — our payload is
                // far under).
                keepalive: true,
            });
        } catch (e) {
            log('Event request failed (placeholder endpoint):', e.message || e);
        }
    }

    // ── Timer math ─────────────────────────────────────────────

    function getTimerMs() {
        return TTB_CONFIG.timerHours * 60 * 60 * 1000;
    }

    function getRemainingMs(state) {
        if (!state || !state.addedAt) return 0;
        return Math.max(0, state.addedAt + getTimerMs() - Date.now());
    }

    function formatTime(ms) {
        if (ms <= 0) return '00:00:00';
        const h = String(Math.floor(ms / 3600000)).padStart(2, '0');
        const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
        const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
        return h + ':' + m + ':' + s;
    }

    // ── Floating banner UI (bottom bar) ────────────────────────

    let floatingBannerEl = null;
    let floatingBarEl = null;

    function createFloatingBanner() {
        if (!TTB_CONFIG.floatingBannerEnabled) return;
        if (floatingBannerEl) return;

        const wrapper = document.createElement('div');
        wrapper.id = 'ttb-floating-banner';
        wrapper.style.cssText = [
            'position: fixed',
            'bottom: 0',
            'left: 0',
            'width: 100%',
            'z-index: 998',
            'opacity: 0.9',
            'background: #111',
            'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            'box-shadow: 0 -2px 12px rgba(0,0,0,0.3)',
        ].join(';');

        // Progress bar track
        const barTrack = document.createElement('div');
        barTrack.style.cssText = 'width: 100%; height: 3px; background: #333;';

        const barFill = document.createElement('div');
        barFill.id = 'ttb-floating-bar';
        barFill.style.cssText = 'height: 100%; background: #e53935; transition: width 1s linear;';
        barTrack.appendChild(barFill);
        wrapper.appendChild(barTrack);

        // Content row: image + text
        const row = document.createElement('div');
        row.style.cssText = 'display: flex; align-items: center; justify-content: center; gap: 10px; padding: 8px 16px;';

        // Product image
        const img = document.createElement('img');
        img.id = 'ttb-floating-img';
        img.alt = 'Free patch';
        img.style.cssText = [
            'width: 36px',
            'height: 36px',
            'object-fit: cover',
            'border-radius: 4px',
            'border: 1px solid #333',
            'flex-shrink: 0',
            'display: none',
        ].join(';');
        row.appendChild(img);

        // Text container with label (white) + timer (red)
        const text = document.createElement('div');
        text.id = 'ttb-floating-text';
        text.style.cssText = [
            'font-size: 13px',
            'font-weight: 600',
            'letter-spacing: 0.03em',
        ].join(';');

        const labelSpan = document.createElement('span');
        labelSpan.id = 'ttb-floating-label';
        labelSpan.style.cssText = 'color: #f5f5f5;';

        const timerSpan = document.createElement('span');
        timerSpan.id = 'ttb-floating-timer';
        timerSpan.style.cssText = 'color: #ef5350; font-weight: 700; font-family: "Courier New", Courier, monospace; font-size: 15px; letter-spacing: 0.08em;';

        text.appendChild(labelSpan);
        text.appendChild(timerSpan);
        row.appendChild(text);

        wrapper.appendChild(row);

        // Dismiss (minimize) button — hides the bar for this session
        const dismissBtn = document.createElement('button');
        dismissBtn.setAttribute('aria-label', 'Dismiss timer');
        dismissBtn.textContent = '✕';
        dismissBtn.style.cssText = [
            'position: absolute',
            'top: 50%',
            'right: 14px',
            'transform: translateY(-50%)',
            'background: rgba(255,255,255,0.12)',
            'border: 1px solid rgba(255,255,255,0.25)',
            'border-radius: 50%',
            'width: 28px',
            'height: 28px',
            'color: rgba(255,255,255,0.7)',
            'font-size: 13px',
            'cursor: pointer',
            'display: flex',
            'align-items: center',
            'justify-content: center',
            'flex-shrink: 0',
            'line-height: 1',
            'padding: 0',
        ].join(';');
        dismissBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            wrapper.style.display = 'none';
            try { localStorage.setItem(TTB_CONFIG.storageKey + '_BDismissed', '1'); } catch (_) {}
        });
        wrapper.appendChild(dismissBtn);

        // If the user previously dismissed the bar, keep it hidden
        try {
            if (localStorage.getItem(TTB_CONFIG.storageKey + '_BDismissed') === '1') {
                wrapper.style.display = 'none';
            }
        } catch (_) {}

        document.body.appendChild(wrapper);
        floatingBannerEl = wrapper;
        floatingBarEl = barFill;

        // Load image from state
        const state = loadState();
        if (state && state.imageUrl) {
            img.src = state.imageUrl;
            img.style.display = 'block';
        } else {
            // Fallback: fetch product image
            fetchPatchImage().then(url => {
                if (url) {
                    img.src = url;
                    img.style.display = 'block';
                }
            });
        }
    }

    async function fetchPatchImage() {
        try {
            const res = await fetch(`/products/${_resolveTtbPatchHandle()}.js`);
            if (!res.ok) return null;
            const product = await res.json();
            return product.featured_image || null;
        } catch (e) {
            return null;
        }
    }

    function updateFloatingBanner(timerText, progressPct) {
        if (!TTB_CONFIG.floatingBannerEnabled) return;
        if (!floatingBannerEl) createFloatingBanner();
        if (!floatingBannerEl) return;

        const labelEl = floatingBannerEl.querySelector('#ttb-floating-label');
        const timerEl = floatingBannerEl.querySelector('#ttb-floating-timer');
        if (labelEl && timerEl) {
            const parts = TTB_CONFIG.floatingBannerText.split('{timer}');
            labelEl.textContent = parts[0] || '';
            timerEl.textContent = timerText + (parts[1] || '');
        }
        if (floatingBarEl) {
            floatingBarEl.style.width = progressPct + '%';
        }
    }

    function removeFloatingBanner() {
        if (floatingBannerEl) {
            floatingBannerEl.remove();
            floatingBannerEl = null;
            floatingBarEl = null;
        }
    }

    // ── Celebration animation (replaces the old modal popup) ──

    let celebrationEl = null;
    let celebrationFlyEl = null;
    let celebrationCountdownInterval = null;

    function injectCelebrationStyles() {
        if (document.getElementById('ttb-celebration-styles')) return;
        const style = document.createElement('style');
        style.id = 'ttb-celebration-styles';
        style.textContent = `
            @keyframes ttb-overlay-in {
                from { opacity: 0; }
                to   { opacity: 1; }
            }
            @keyframes ttb-glow-in {
                0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.1); }
                65%  { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
                100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
            }
            @keyframes ttb-patch-in {
                0%   { opacity: 0; transform: scale(0.1) rotate(-20deg); }
                65%  { transform: scale(1.09) rotate(3deg); }
                100% { opacity: 1; transform: scale(1) rotate(0deg); }
            }
            @keyframes ttb-text-in {
                from { opacity: 0; transform: translateY(12px); }
                to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes ttb-text-out {
                from { opacity: 1; transform: translateY(0); }
                to   { opacity: 0; transform: translateY(-16px); }
            }
            @keyframes ttb-sparkle-burst {
                0%   { opacity: 0; transform: scale(0) rotate(0deg); }
                40%  { opacity: 1; }
                100% { opacity: 0; transform: scale(1.8) rotate(120deg); }
            }
            @keyframes ttb-ring-pulse {
                0%   { opacity: 0.7; transform: translate(-50%, -50%) scale(0.7); }
                100% { opacity: 0; transform: translate(-50%, -50%) scale(1.7); }
            }
        `;
        document.head.appendChild(style);
    }

    // Canvas-based white background removal.
    // Returns a data URL with near-white pixels made transparent,
    // or null if the image is cross-origin and the browser blocks canvas reads.
    function removeWhiteFromImage(src, threshold) {
        if (threshold === undefined) threshold = 210;
        return new Promise(function (resolve) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = function () {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width  = img.naturalWidth  || img.width;
                    canvas.height = img.naturalHeight || img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const id  = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const px  = id.data;
                    const range = 255 - threshold;
                    for (let i = 0; i < px.length; i += 4) {
                        const r = px[i], g = px[i + 1], b = px[i + 2];
                        if (r >= threshold && g >= threshold && b >= threshold) {
                            // Feathered alpha so edges look soft, not hard-cut
                            const whiteness = Math.min(r, g, b);
                            px[i + 3] = range > 0
                                ? Math.max(0, Math.round((255 - whiteness) * (255 / range)))
                                : 0;
                        }
                    }
                    ctx.putImageData(id, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                } catch (e) {
                    log('Canvas white removal failed (CORS?):', e.message);
                    resolve(null);
                }
            };
            img.onerror = function () { resolve(null); };
            img.src = src;
        });
    }

    function hideCelebrationAnimation() {
        if (celebrationCountdownInterval) {
            clearInterval(celebrationCountdownInterval);
            celebrationCountdownInterval = null;
        }
        if (celebrationFlyEl) {
            celebrationFlyEl.remove();
            celebrationFlyEl = null;
        }
        if (celebrationEl) {
            celebrationEl.remove();
            celebrationEl = null;
        }
    }

    async function showCelebrationAnimation(imageUrl) {
        if (!TTB_CONFIG.activationPopupEnabled) return;
        if (celebrationEl) return;

        injectCelebrationStyles();

        // Try to strip the white background via canvas processing. Falls
        // back gracefully if the CDN blocks cross-origin canvas reads.
        let processedSrc = null;
        if (imageUrl) {
            processedSrc = await removeWhiteFromImage(imageUrl);
        }
        const bgRemoved = !!processedSrc;

        // Guard: something else may have triggered while we were awaiting
        if (celebrationEl) return;

        // ── Overlay (clickable — tap anywhere to fly to cart) ─────
        const overlay = document.createElement('div');
        overlay.id = 'ttb-celebration';
        overlay.style.cssText = [
            'position:fixed',
            'inset:0',
            'z-index:99999',
            'background:rgba(0,0,0,0.88)',
            'animation:ttb-overlay-in 0.5s ease forwards',
            'overflow:hidden',
            'cursor:pointer',
        ].join(';');

        // ── Ambient radial glow ───────────────────────────────────
        const ambientGlow = document.createElement('div');
        ambientGlow.style.cssText = [
            'position:absolute',
            'top:50%',
            'left:50%',
            'width:620px',
            'height:620px',
            'background:radial-gradient(circle, rgba(255,200,30,0.28) 0%, rgba(255,160,0,0.12) 50%, transparent 72%)',
            'border-radius:50%',
            'transform:translate(-50%,-50%) scale(0.1)',
            'opacity:0',
            'pointer-events:none',
            'animation:ttb-glow-in 1s 0.1s ease forwards',
        ].join(';');
        overlay.appendChild(ambientGlow);

        // ── Pulsing ring ──────────────────────────────────────────
        const ring = document.createElement('div');
        ring.style.cssText = [
            'position:absolute',
            'top:50%',
            'left:50%',
            'width:270px',
            'height:270px',
            'border:2px solid rgba(255,215,0,0.55)',
            'border-radius:50%',
            'transform:translate(-50%,-50%)',
            'pointer-events:none',
            'animation:ttb-ring-pulse 1.4s 0.55s ease-out forwards',
        ].join(';');
        overlay.appendChild(ring);

        // ── Sparkles ──────────────────────────────────────────────
        [
            { t: '27%', l: '31%', d: '0.5s',  s: '22px' },
            { t: '21%', l: '58%', d: '0.65s', s: '16px' },
            { t: '69%', l: '37%', d: '0.55s', s: '18px' },
            { t: '73%', l: '62%', d: '0.72s', s: '20px' },
            { t: '31%', l: '67%', d: '0.45s', s: '14px' },
            { t: '61%', l: '27%', d: '0.6s',  s: '12px' },
        ].forEach(sp => {
            const el = document.createElement('div');
            el.textContent = '✦';
            el.style.cssText = [
                'position:absolute',
                `top:${sp.t}`,
                `left:${sp.l}`,
                `font-size:${sp.s}`,
                'color:#ffd700',
                'opacity:0',
                'pointer-events:none',
                `animation:ttb-sparkle-burst 0.95s ${sp.d} ease forwards`,
            ].join(';');
            overlay.appendChild(el);
        });

        // ── Content (title + patch + subtitle) ────────────────────
        const content = document.createElement('div');
        content.id = 'ttb-celebration-content';
        content.style.cssText = [
            'position:absolute',
            'top:50%',
            'left:50%',
            'transform:translate(-50%,-50%)',
            'display:flex',
            'flex-direction:column',
            'align-items:center',
            'gap:18px',
            'text-align:center',
            'width:min(90vw, 340px)',
            'pointer-events:none',
        ].join(';');

        // Title
        const titleEl = document.createElement('div');
        titleEl.id = 'ttb-cel-title';
        titleEl.textContent = 'Limited edition patch unlocked!';
        titleEl.style.cssText = [
            'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
            'font-size:clamp(30px, 6vw, 38px)',
            'font-weight:800',
            'color:#ffd700',
            'text-shadow:0 0 28px rgba(255,215,0,0.95), 0 2px 6px rgba(0,0,0,0.6)',
            'letter-spacing:0.01em',
            'line-height:1.2',
            'opacity:0',
            'animation:ttb-text-in 0.45s 0.85s ease forwards',
        ].join(';');
        content.appendChild(titleEl);

        // Patch stage
        // If canvas removal worked → transparent bg + drop-shadow glow.
        // If CORS blocked it   → white circle + box-shadow (original fallback).
        const patchStage = document.createElement('div');
        patchStage.id = 'ttb-cel-patch';
        patchStage.style.cssText = bgRemoved ? [
            'width:190px',
            'height:190px',
            'border-radius:50%',
            'overflow:hidden',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'flex-shrink:0',
            'filter:drop-shadow(0 0 24px rgba(255,210,30,1)) drop-shadow(0 0 48px rgba(255,160,0,0.65))',
            'opacity:0',
            'animation:ttb-patch-in 0.55s 0.55s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
        ].join(';') : [
            'width:190px',
            'height:190px',
            'background:#ffffff',
            'border-radius:50%',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'flex-shrink:0',
            'box-shadow:0 0 0 8px rgba(255,215,0,0.15), 0 0 55px 18px rgba(255,200,30,0.6)',
            'opacity:0',
            'animation:ttb-patch-in 0.55s 0.55s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
        ].join(';');

        if (imageUrl) {
            const img = document.createElement('img');
            img.src = bgRemoved ? processedSrc : imageUrl;
            img.alt = 'Free patch';
            img.draggable = false;
            img.style.cssText = bgRemoved
                ? 'width:190px;height:190px;object-fit:contain;display:block;transform:scale(2);'
                : 'width:180px;height:180px;object-fit:contain;mix-blend-mode:multiply;border-radius:50%;display:block;';
            patchStage.appendChild(img);
        } else {
            patchStage.innerHTML = '<div style="font-size:72px;line-height:1;">🎖️</div>';
        }
        content.appendChild(patchStage);

        // Subtitle: static intro text
        const subtitleEl = document.createElement('div');
        subtitleEl.id = 'ttb-cel-subtitle';
        subtitleEl.style.cssText = [
            'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
            'font-size:clamp(12px, 3vw, 14px)',
            'font-weight:500',
            'color:rgba(255,255,255,0.72)',
            'line-height:1.4',
            'max-width:270px',
            'opacity:0',
            'animation:ttb-text-in 0.45s 1.05s ease forwards',
        ].join(';');
        subtitleEl.textContent = "Complete checkout in time or it's gone forever!";
        content.appendChild(subtitleEl);

        // Big red live countdown
        const subtitleTimerEl = document.createElement('div');
        subtitleTimerEl.id = 'ttb-cel-timer';
        subtitleTimerEl.style.cssText = [
            'font-family:"Courier New",Courier,monospace',
            'font-size:clamp(32px, 7.5vw, 44px)',
            'font-weight:800',
            'color:#ef5350',
            'letter-spacing:0.12em',
            'text-shadow:0 0 20px rgba(239,83,80,0.6)',
            'margin-top:-4px',
            'opacity:0',
            'animation:ttb-text-in 0.45s 1.15s ease forwards',
        ].join(';');

        function updateSubtitle() {
            const state = loadState();
            const remaining = state ? getRemainingMs(state) : getTimerMs();
            subtitleTimerEl.textContent = formatTime(remaining);
        }
        updateSubtitle();
        content.appendChild(subtitleTimerEl);

        // "Tap to continue" hint — prominent pill button style
        const tapHint = document.createElement('div');
        tapHint.style.cssText = [
            'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
            'font-size: 18px',
            'font-weight:700',
            'color:#111',
            'letter-spacing:0.04em',
            'text-transform:uppercase',
            'background:#ffffff',
            'border:none',
            'padding:9px 22px',
            'border-radius:999px',
            'margin-top:4px',
            'opacity:0',
            'animation:ttb-text-in 0.4s 1.7s ease forwards',
        ].join(';');
        tapHint.textContent = 'Tap anywhere to continue';
        content.appendChild(tapHint);

        overlay.appendChild(content);

        // X close button — top-right corner of overlay
        const closeBtn = document.createElement('button');
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.textContent = '✕';
        closeBtn.style.cssText = [
            'position:absolute',
            'top:18px',
            'right:18px',
            'width:60px',
            'height:60px',
            'border:none',
            'border-radius:50%',
            'background:#ffffff',
            'color:#111',
            'font-size:23px',
            'cursor:pointer',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'opacity:0',
            'animation:ttb-text-in 0.4s 0.8s ease forwards',
        ].join(';');
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation(); // don't double-fire with overlay click
            triggerFly();
        });
        overlay.appendChild(closeBtn);

        document.body.appendChild(overlay);
        celebrationEl = overlay;

        // Live subtitle countdown while visible
        celebrationCountdownInterval = setInterval(() => {
            if (!celebrationEl) { clearInterval(celebrationCountdownInterval); return; }
            updateSubtitle();
        }, 1000);

        // ── Fly-to-cart logic (triggered by click or 30s safety) ──
        let hasFired = false;

        function triggerFly() {
            if (hasFired || !celebrationEl) return;
            hasFired = true;

            if (celebrationCountdownInterval) {
                clearInterval(celebrationCountdownInterval);
                celebrationCountdownInterval = null;
            }

            // Fade out text first
            const t  = document.getElementById('ttb-cel-title');
            const s  = document.getElementById('ttb-cel-subtitle');
            const tm = document.getElementById('ttb-cel-timer');
            if (t)  t.style.animation  = 'ttb-text-out 0.3s ease forwards';
            if (s)  s.style.animation  = 'ttb-text-out 0.3s 0.04s ease forwards';
            if (tm) tm.style.animation = 'ttb-text-out 0.3s 0.06s ease forwards';
            tapHint.style.animation = 'ttb-text-out 0.2s ease forwards';

            // Launch fly clone after brief text fade
            setTimeout(() => {
                if (!celebrationEl) return;

                const patchEl = document.getElementById('ttb-cel-patch');
                if (!patchEl) { hideCelebrationAnimation(); return; }

                const rect = patchEl.getBoundingClientRect();

                const flyEl = document.createElement('div');
                flyEl.style.cssText = [
                    'position:fixed',
                    `left:${rect.left}px`,
                    `top:${rect.top}px`,
                    `width:${rect.width}px`,
                    `height:${rect.height}px`,
                    'border-radius:50%',
                    bgRemoved ? 'background:transparent' : 'background:#ffffff',
                    'display:flex',
                    'align-items:center',
                    'justify-content:center',
                    bgRemoved
                        ? 'filter:drop-shadow(0 0 14px rgba(255,200,30,0.85))'
                        : 'box-shadow:0 0 32px 12px rgba(255,200,30,0.55)',
                    'z-index:100000',
                    'pointer-events:none',
                    'transition:left 0.6s cubic-bezier(0.4,0,0.2,1), top 0.6s cubic-bezier(0.4,0,0.2,1), width 0.6s ease, height 0.6s ease, opacity 0.28s 0.34s ease',
                ].join(';');

                if (imageUrl) {
                    const flyImg = document.createElement('img');
                    flyImg.src = bgRemoved ? processedSrc : imageUrl;
                    flyImg.alt = '';
                    flyImg.draggable = false;
                    flyImg.style.cssText = bgRemoved
                        ? 'width:90%;height:90%;object-fit:contain;display:block;'
                        : 'width:80%;height:80%;object-fit:contain;mix-blend-mode:multiply;border-radius:50%;display:block;';
                    flyEl.appendChild(flyImg);
                }

                document.body.appendChild(flyEl);
                celebrationFlyEl = flyEl;

                // Fade overlay out simultaneously
                celebrationEl.style.transition = 'opacity 0.35s ease';
                celebrationEl.style.opacity = '0';

                // Two rAF frames ensure the starting position is painted first
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        flyEl.style.left   = (window.innerWidth - 52) + 'px';
                        flyEl.style.top    = '22px';
                        flyEl.style.width  = '30px';
                        flyEl.style.height = '30px';
                        flyEl.style.opacity = '0';
                    });
                });

                setTimeout(() => hideCelebrationAnimation(), 1000);
            }, 320);
        }

        // Click / tap anywhere fires the fly
        overlay.addEventListener('click', triggerFly);

        // Safety: auto-dismiss after 15s if user never interacts
        setTimeout(() => {
            if (celebrationEl && !hasFired) triggerFly();
        }, 15000);
    }

    // ── Line-item timer UI ─────────────────────────────────────

    function applyTimerToLineItem(lineItem, timerText, progressPct) {
        // ── Festive line-item styling (applied once) ──────────────
        if (!lineItem.dataset.ttbStyled) {
            lineItem.dataset.ttbStyled = '1';
            // Snapshot originals so we can restore them cleanly later
            lineItem.dataset.ttbOrigBorder     = lineItem.style.border     || '';
            lineItem.dataset.ttbOrigRadius     = lineItem.style.borderRadius || '';
            lineItem.dataset.ttbOrigPadding    = lineItem.style.padding    || '';
            lineItem.dataset.ttbOrigBackground = lineItem.style.background || '';
            lineItem.dataset.ttbOrigBoxSizing  = lineItem.style.boxSizing  || '';

            lineItem.style.border      = '1.5px solid rgba(255, 200, 30, 0.6)';
            lineItem.style.borderRadius = '8px';
            lineItem.style.padding     = '8px';
            lineItem.style.background  = 'linear-gradient(135deg, rgba(255,215,0,0.07) 0%, rgba(255,200,30,0.03) 100%)';
            lineItem.style.boxSizing   = 'border-box';
        }

        // ── Hide qty stepper, keep the remove link visible ────────
        // <line-item-quantity> contains both <quantity-selector> and the remove <a>.
        // Hide only the stepper so the delete link stays accessible.
        const qtyEl = lineItem.querySelector('line-item-quantity');
        if (qtyEl) {
            const qtySelectorEl = qtyEl.querySelector('quantity-selector');
            if (qtySelectorEl) qtySelectorEl.style.display = 'none';
        }

        // ── Timer banner ──────────────────────────────────────────
        let banner = lineItem.querySelector('.free-patch-timer-banner');
        if (!banner) {
            banner = document.createElement('div');
            banner.className = 'free-patch-timer-banner';
            if (qtyEl && qtyEl.parentNode) {
                qtyEl.parentNode.insertBefore(banner, qtyEl);
            } else {
                lineItem.appendChild(banner);
            }
        }

        // Move banner to sit just before the qty selector — only if not already there
        if (qtyEl && qtyEl.parentNode && qtyEl.previousElementSibling !== banner) {
            qtyEl.parentNode.insertBefore(banner, qtyEl);
        }

        // Apply static styles only once to avoid repaint on every tick
        if (!banner.dataset.ttbBannerStyled) {
            banner.dataset.ttbBannerStyled = '1';
            banner.style.cssText = [
                'display: block',
                'font-size: 11px',
                'font-weight: 600',
                'letter-spacing: 0.03em',
                'padding: 5px 0 3px',
                'margin-top: 6px',
            ].join(';');
        }
        // Always ensure the banner is visible — hideLineItemTimers() sets display:none,
        // but the guard above would skip re-applying display:block on subsequent ticks.
        banner.style.display = 'block';

        // Build inner HTML on first run
        let barEl   = banner.querySelector('.ttb-line-bar');
        let labelEl = banner.querySelector('.ttb-line-label');
        let timerEl = banner.querySelector('.ttb-line-timer');

        if (!labelEl) {
            banner.innerHTML = '';

            const textRow = document.createElement('div');

            labelEl = document.createElement('span');
            labelEl.className = 'ttb-line-label';
            labelEl.style.cssText = 'color: #333;';

            timerEl = document.createElement('span');
            timerEl.className = 'ttb-line-timer';
            timerEl.style.cssText = 'color: #ef5350; font-weight: 700; font-family: "Courier New", Courier, monospace; font-size: 16px; letter-spacing: 0.08em; display: block; margin-top: 3px;';

            textRow.appendChild(labelEl);
            textRow.appendChild(timerEl);
            banner.appendChild(textRow);

            const barTrack = document.createElement('div');
            barTrack.style.cssText = 'width: 100%; height: 3px; background: #e8e8e8; border-radius: 2px; margin-top: 5px; margin-bottom: 2px;';
            barEl = document.createElement('div');
            barEl.className = 'ttb-line-bar';
            barEl.style.cssText = 'height: 100%; background: #ef5350; border-radius: 2px; transition: width 1s linear;';
            barTrack.appendChild(barEl);
            banner.appendChild(barTrack);
        }

        const parts = TTB_CONFIG.lineItemTimerText.split('{timer}');
        labelEl.textContent = parts[0] || '';
        timerEl.textContent = timerText + (parts[1] || '');
        if (barEl) barEl.style.width = progressPct + '%';

        // Hide the inline timer slot — only use banner
        const inline = lineItem.querySelector('.free-patch-timer-inline');
        if (inline) inline.style.display = 'none';
    }

    let lastTimerArgs = null;

    function updateLineItemTimers(timerText, progressPct) {
        if (!TTB_CONFIG.lineItemTimerEnabled) return;
        lastTimerArgs = [timerText, progressPct];

        const handle = _resolveTtbPatchHandle();

        // Find patch line items via data-product-handle on <line-item>
        document.querySelectorAll(`line-item[data-product-handle="${handle}"]`).forEach(li => {
            applyTimerToLineItem(li, timerText, progressPct);
        });

        // Fallback via .cart-low-stock-variant
        document.querySelectorAll(`.cart-low-stock-variant[data-product-handle="${handle}"]`).forEach(div => {
            const li = div.closest('line-item');
            if (li) applyTimerToLineItem(li, timerText, progressPct);
        });
    }

    function hideLineItemTimers() {
        document.querySelectorAll('.free-patch-timer-banner').forEach(el => {
            el.style.display = 'none';
            el.innerHTML = '';
        });
        document.querySelectorAll('.free-patch-timer-inline').forEach(el => {
            el.style.display = 'none';
        });

        // Restore any line-items we decorated with festive styling
        document.querySelectorAll('line-item[data-ttb-styled]').forEach(li => {
            li.style.border       = li.dataset.ttbOrigBorder     || '';
            li.style.borderRadius = li.dataset.ttbOrigRadius      || '';
            li.style.padding      = li.dataset.ttbOrigPadding     || '';
            li.style.background   = li.dataset.ttbOrigBackground  || '';
            li.style.boxSizing    = li.dataset.ttbOrigBoxSizing   || '';
            delete li.dataset.ttbStyled;
            delete li.dataset.ttbOrigBorder;
            delete li.dataset.ttbOrigRadius;
            delete li.dataset.ttbOrigPadding;
            delete li.dataset.ttbOrigBackground;
            delete li.dataset.ttbOrigBoxSizing;

            const qtyEl = li.querySelector('line-item-quantity');
            if (qtyEl) {
                const qtySelectorEl = qtyEl.querySelector('quantity-selector');
                if (qtySelectorEl) qtySelectorEl.style.display = '';
            }
        });
    }

    // ── Core engine ────────────────────────────────────────────

    let tickInterval = null;
    let lineItemObserver = null;
    let isAdding = false;

    function tick() {
        const state = loadState();
        if (!state || !state.active) {
            stopTimer();
            return;
        }

        const remaining = getRemainingMs(state);

        if (remaining <= 0) {
            onExpired(state);
            return;
        }

        const timerText = formatTime(remaining);
        const progressPct = Math.max(0, Math.min(100, (remaining / getTimerMs()) * 100));
        updateFloatingBanner(timerText, progressPct);
        updateLineItemTimers(timerText, progressPct);
    }

    function startLineItemObserver() {
        if (lineItemObserver) return;
        const handle = _resolveTtbPatchHandle();
        lineItemObserver = new MutationObserver(function (mutations) {
            let found = false;
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType !== 1) continue;
                    if (
                        (node.matches && node.matches(`line-item[data-product-handle="${handle}"]`)) ||
                        (node.querySelector && node.querySelector(`line-item[data-product-handle="${handle}"]`))
                    ) {
                        found = true;
                        break;
                    }
                }
                if (found) break;
            }
            if (found && lastTimerArgs) updateLineItemTimers.apply(null, lastTimerArgs);
        });
        lineItemObserver.observe(document.body, { childList: true, subtree: true });
    }

    function stopLineItemObserver() {
        if (lineItemObserver) {
            lineItemObserver.disconnect();
            lineItemObserver = null;
        }
    }

    function startTimer() {
        if (tickInterval) return;
        tick(); // immediate first tick
        tickInterval = setInterval(tick, 1000);
        startLineItemObserver();
        log('Timer started');
    }

    function stopTimer() {
        if (tickInterval) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
        stopLineItemObserver();
    }

    async function onExpired(state) {
        log('Timer expired');
        stopTimer();

        if (TTB_CONFIG.removeOnExpiry && state.patchLineKey) {
            await removePatchFromCart(state.patchLineKey);
        }

        clearState();
        removeFloatingBanner();
        hideLineItemTimers();
        hideCelebrationAnimation();

        // Refresh cart UI so the removed patch disappears immediately
        document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async function activate(triggeringItem) {
        if (isAdding) return;
        // Per-flow enable gate. __userFlow is resolved by cart-add time (unlike
        // module-eval), so we branch HERE, not at the module-level master gate.
        // `!== false` => an absent/old-cache value is treated as ON (retro-compat).
        var _isReturningUser = (typeof window !== 'undefined' && window.__userFlow === 'returning-user');
        var _flowEnabled = _isReturningUser
            ? (TTB_CONFIG.enabledReturning !== false)
            : (TTB_CONFIG.enabledNewUser !== false);
        if (!_flowEnabled) {
            log('TTB skipped: ' + (_isReturningUser ? 'returning-buyer' : 'new-user') + ' flow disabled in config');
            return;
        }
        if (isUsed()) {
            log('User already used promotion, skipping');
            return;
        }

        // TTB triggers ONLY on a PAID product add (2026-06-04 user rule). Skip when:
        //  • there's no identified triggering product (triggeringItem == null) — e.g.
        //    the cart change was just the free patch / scorpion patch (the patch handle
        //    is excluded from trigger detection in the cart:change diff), or a qty bump;
        //  • the triggering product is FREE (price 0, in cents) — the scorpion patch,
        //    the free patch itself, or any $0 item.
        // So no free/patch add ever (re)starts or re-arms the promo. A non-patch PAID
        // product always yields a non-null, price>0 triggeringItem, so it's unaffected.
        // Placed BEFORE the isAdding latch (clean early exit). Fail-open on price: a
        // MISSING price (NaN) is NOT treated as free, so a legit paid trigger whose
        // event omitted price still fires.
        if (!triggeringItem || Number(triggeringItem.price) === 0) {
            log('Skipping TTB: no paid triggering product (free / patch-only / qty-bump add)');
            return;
        }

        isAdding = true;
        log('Activating promotion...');

        try {
            const timerStartedAt = Date.now();
            const patchHandle = _resolveTtbPatchHandle();

            // Fetch cart first — needed for exclusion check and patch-already-present check
            const cart = await getCart();

            // Skip if the triggering item OR any existing non-patch cart item is from
            // the excluded collection. One fetch covers all items.
            const existingNonPatchItems = (cart?.items || []).filter(i =>
                (i.product_handle || i.handle) !== patchHandle
            );
            const itemsToCheck = triggeringItem
                ? [triggeringItem, ...existingNonPatchItems]
                : existingNonPatchItems;

            // Panel-managed blacklist (sync, no fetch): if ANY cart item's handle
            // is in TTB_CONFIG.excludedTriggerHandles, TTB never fires — same
            // cart-wide suppression as the excluded-collection check below.
            const excludedHandles = Array.isArray(TTB_CONFIG.excludedTriggerHandles)
                ? TTB_CONFIG.excludedTriggerHandles : [];
            if (excludedHandles.length > 0 && itemsToCheck.some(i =>
                excludedHandles.indexOf((i.product_handle || i.handle || '').toLowerCase()) !== -1
            )) {
                log('Skipping: a cart item is in the panel excludedTriggerHandles blacklist');
                return;
            }

            // Panel-managed collection blacklist: if ANY cart item is a member of
            // any collection in excludedTriggerCollections, TTB never fires. The
            // membership lookups are cached + fail-open (a fetch error never
            // suppresses a legit patch).
            const excludedCollections = Array.isArray(TTB_CONFIG.excludedTriggerCollections)
                ? TTB_CONFIG.excludedTriggerCollections : [];
            if (excludedCollections.length > 0 && itemsToCheck.length > 0) {
                const itemHandles = itemsToCheck.map(i =>
                    String(i.product_handle || i.handle || '').trim().toLowerCase()
                ).filter(Boolean);
                if (itemHandles.length > 0) {
                    const sets = await Promise.all(excludedCollections.map(c => _ttbCollectionHandleSet(c)));
                    if (sets.some(set => set.size && itemHandles.some(h => set.has(h)))) {
                        log('Skipping: a cart item is in a panel excludedTriggerCollections collection');
                        return;
                    }
                }
            }

            // Panel-managed tag blacklist: if ANY cart item carries any tag in
            // excludedTriggerTags, TTB never fires. Per-product tag lookups are
            // cached + fail-open.
            const excludedTags = Array.isArray(TTB_CONFIG.excludedTriggerTags)
                ? TTB_CONFIG.excludedTriggerTags : [];
            if (excludedTags.length > 0 && itemsToCheck.length > 0) {
                const itemHandles = itemsToCheck.map(i =>
                    String(i.product_handle || i.handle || '').trim().toLowerCase()
                ).filter(Boolean);
                if (itemHandles.length > 0) {
                    const excludedTagSet = new Set(excludedTags);
                    const tagLists = await Promise.all(itemHandles.map(h => _ttbProductTags(h)));
                    if (tagLists.some(tags => tags.some(t => excludedTagSet.has(t)))) {
                        log('Skipping: a cart item carries a panel excludedTriggerTags tag');
                        return;
                    }
                }
            }

            if (itemsToCheck.length > 0) {
                const anyExcluded = await isAnyItemInExcludedCollection(itemsToCheck);
                if (anyExcluded) {
                    log('Skipping: triggering item or cart item belongs to excluded collection');
                    return;
                }
            }

            // Check if patch is already in cart
            let lineKey = null;
            let imageUrl = '';
            let trackingCart = cart;

            if (cart && cart.items) {
                const existing = cart.items.find(i => (i.handle === patchHandle || i.product_handle === patchHandle));
                if (existing) {
                    lineKey = existing.key;
                    imageUrl = existing.featured_image?.url || existing.image || '';
                    log('Patch already in cart');
                }
            }

            if (!lineKey) {
                const result = await addPatchToCart(triggeringItem, timerStartedAt);
                if (!result) {
                    log('Failed to add patch to cart');
                    isAdding = false;
                    return;
                }
                lineKey = result.lineKey;
                imageUrl = result.imageUrl || '';
                trackingCart = result.cart || cart;
            }

            // Save state & flag
            const state = {
                addedAt: timerStartedAt,
                patchLineKey: lineKey,
                imageUrl: imageUrl,
                triggeringProduct: triggeringItem ? {
                    handle: triggeringItem.product_handle || triggeringItem.handle || '',
                    title: triggeringItem.product_title || triggeringItem.title || '',
                    variant_id: triggeringItem.variant_id || null,
                } : null,
                active: true,
            };
            saveState(state);
            markUsed();

            // Fire tracking event — async, does not block the activation flow
            sendActivationTrackingEvent(state, triggeringItem, trackingCart, lineKey, imageUrl);

            // Start countdown
            startTimer();

            // Refresh cart UI (drawer, icon count, etc.) from server
            document.dispatchEvent(new CustomEvent('cart:refresh'));

            // Show celebration animation then start ticking
            setTimeout(() => {
                showCelebrationAnimation(imageUrl);
                tick();
            }, 400);

            log('Promotion activated', state);
        } catch (e) {
            log('Error activating', e);
        } finally {
            isAdding = false;
        }
    }

    // ── Event listeners ────────────────────────────────────────

    function bindEvents() {
        function scheduleActivationOrTick(triggeringItem) {
            const state = loadState();

            // If promotion is already active, only re-render timers in fresh DOM.
            if (state && state.active && getRemainingMs(state) > 0) {
                setTimeout(() => tick(), 200);
                return;
            }

            setTimeout(() => activate(triggeringItem), 300);
        }

        let previousCartItemCount = null;
        let previousCartItemKeys = new Set();

        // Initialize baseline count and item keys so we can detect true additions.
        getCart().then((cart) => {
            if (cart && typeof cart.item_count === 'number') {
                previousCartItemCount = cart.item_count;
                previousCartItemKeys = new Set((cart.items || []).map(i => i.key));
            }
        });

        // Cart events only: activate when cart count increases.
        document.addEventListener('cart:change', (e) => {
            const baseEvent = e.detail?.baseEvent || '';

            // Skip our own events to avoid loops
            if (baseEvent.startsWith('ttb:')) return;

            const currentCount = Number(e.detail?.cart?.item_count);
            if (!Number.isFinite(currentCount)) {
                log('cart:change ignored (missing item_count), baseEvent:', baseEvent);
                return;
            }

            // Identify the newly added item by diffing against known item keys.
            // Exclude the patch itself so we don't self-reference.
            const currentItems = e.detail?.cart?.items || [];
            const patchHandle = _resolveTtbPatchHandle();
            const triggeringItem = currentItems.find(i =>
                !previousCartItemKeys.has(i.key) &&
                (i.product_handle || i.handle) !== patchHandle
            ) || null;
            previousCartItemKeys = new Set(currentItems.map(i => i.key));

            // First observed cart event: set baseline and only treat as add if it
            // doesn't look like a line-item update.
            if (previousCartItemCount === null) {
                const shouldTreatAsAdd = baseEvent !== 'line-item:change' && currentCount > 0;
                previousCartItemCount = currentCount;

                if (!shouldTreatAsAdd) {
                    log('cart:change baseline set, baseEvent:', baseEvent, 'count:', currentCount);
                    return;
                }

                log('cart:change add detected (baseline), baseEvent:', baseEvent, 'count:', currentCount);
                scheduleActivationOrTick(triggeringItem);
                return;
            }

            const isAdd = currentCount > previousCartItemCount;
            log('cart:change detected, baseEvent:', baseEvent, 'prevCount:', previousCartItemCount, 'currentCount:', currentCount, 'isAdd:', isAdd);
            previousCartItemCount = currentCount;

            if (!isAdd) return;

            scheduleActivationOrTick(triggeringItem);
        });

        // Re-apply timer after any cart re-render (many themes dispatch this on drawer open)
        document.addEventListener('cart:refresh', () => {
            const state = loadState();
            if (state && state.active && getRemainingMs(state) > 0) {
                setTimeout(() => tick(), 500);
            }
        });

        // Stamp time-remaining on the patch line item properties at checkout.
        // Runs as a background fire-and-forget — must NOT block or intercept the
        // checkout navigation (doing so broke checkout for new users with active TTB).
        document.addEventListener('click', (e) => {
            const checkoutTarget = e.target.closest(
                'a[href*="/checkout"], button[name="checkout"], input[type="submit"][name="checkout"]'
            );
            if (!checkoutTarget) return;

            const state = loadState();
            if (!state || !state.active) return;

            const remaining = getRemainingMs(state);
            if (remaining <= 0) return;

            // Fire-and-forget — do NOT call e.preventDefault() or block navigation
            (async () => {
                try {
                    const cartData = await getCart();
                    const patchItem = cartData?.items?.find(i => i.key === state.patchLineKey);
                    const existingProps = patchItem?.properties || {};
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        body: JSON.stringify({
                            id: state.patchLineKey,
                            properties: {
                                ...existingProps,
                                '_ttb_checkout_time_left': formatTime(remaining),
                                '_ttb_checked_out_at': new Date().toISOString(),
                            },
                        }),
                    });
                } catch (ex) {
                    // best effort — checkout already proceeding
                }
            })();
        }, true); // true = capture phase

        // If user manually removes the patch from cart
        document.addEventListener('line-item:change', (e) => {
            if (e.detail && e.detail.quantity === 0) {
                const state = loadState();
                if (!state || !state.active) return;

                // Check if the removed item was our patch
                const lineKeyEl = e.target?.closest?.('line-item')?.querySelector('input[data-line-key]');
                const lineKey = lineKeyEl?.dataset?.lineKey;

                if (lineKey && lineKey === state.patchLineKey) {
                    log('Patch manually removed by user');
                    stopTimer();
                    clearState();
                    removeFloatingBanner();
                    hideLineItemTimers();
                    hideCelebrationAnimation();
                }
            }
        });
    }

    // ── Init ───────────────────────────────────────────────────

    function init() {
        log('Initializing...');

        const state = loadState();

        if (state && state.active) {
            const remaining = getRemainingMs(state);

            if (remaining <= 0) {
                // Expired while user was away
                log('Promotion expired while away');
                onExpired(state);
            } else {
                // Resume active timer
                log('Resuming active promotion, remaining:', formatTime(remaining));
                startTimer();
            }
        }

        bindEvents();
        log('Ready');
    }

    // Wait for DOM
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose for console debugging
    window.TTB = {
        getState: loadState,
        getConfig: () => TTB_CONFIG,
        isUsed,
        reset: () => {
            stopTimer();
            clearState();
            removeFloatingBanner();
            hideLineItemTimers();
            hideCelebrationAnimation();
            try { localStorage.removeItem(TTB_CONFIG.flagKey); } catch (e) {}
            try { localStorage.removeItem(TTB_CONFIG.returningUserFlagKey); } catch (e) {} // clear returning-user flag too
            log('State and flag reset');
        },
        // ── Design testing ────────────────────────────────────────
        // Open the modal popup with a custom or auto-detected image.
        // Usage in browser console:
        //   TTB.showModal()                    — uses saved state image
        //   TTB.showModal('https://...')       — uses a custom image URL
        showModal: (imageUrl) => {
            hideCelebrationAnimation(); // clear any existing instance first
            const state = loadState();
            const url = imageUrl || (state && state.imageUrl) || '';
            showCelebrationAnimation(url);
        },
    };
})();
